import { EventBus } from "../EventBus.js";
import {
  AUTOSAVE_SLOT_ID,
  save,
  type SaveSlotInput,
  type SaveSlotSummary,
} from "./PersistenceManager.js";

/**
 * Autosave triggers (P05).
 *
 * Subscribes to the four gameplay events that the persistence spec commits
 * to as autosave points:
 *   - `save:autosave_requested` (Ink `autosave()` external)
 *   - `world:long_rest_completed`
 *   - `world:floor_entered`
 *   - `world:quest_completed`
 *
 * Each fires an independent write to the reserved `autosave` slot. The
 * caller passes an `inputProvider` that reads the live engine state at
 * fire time so `Autosaver` stays decoupled from the world model.
 *
 * Failures are reported to an optional `onError` callback rather than
 * thrown — a failed autosave should never crash gameplay.
 */

export interface AutosaverOptions {
  /** Resolves a fresh `SaveSlotInput` snapshot at autosave time. */
  readonly inputProvider: () => SaveSlotInput;
  /** Optional hook fired after each successful autosave. */
  readonly onSuccess?: (summary: SaveSlotSummary, reason: AutosaveReason) => void;
  /** Optional hook fired if the write throws. */
  readonly onError?: (error: unknown, reason: AutosaveReason) => void;
  /** Override for tests. Defaults to the real `save()` from PersistenceManager. */
  readonly saveImpl?: (slotId: string, input: SaveSlotInput) => Promise<SaveSlotSummary>;
}

export type AutosaveReason =
  | "ink_autosave"
  | "long_rest"
  | "floor_entered"
  | "quest_completed";

export interface Autosaver {
  readonly dispose: () => void;
  /** Run an autosave outside the event flow (tests + manual triggers). */
  readonly trigger: (reason: AutosaveReason) => Promise<void>;
}

export function createAutosaver(options: AutosaverOptions): Autosaver {
  const saveImpl = options.saveImpl ?? save;

  const runAutosave = async (reason: AutosaveReason): Promise<void> => {
    try {
      const input = options.inputProvider();
      const summary = await saveImpl(AUTOSAVE_SLOT_ID, input);
      options.onSuccess?.(summary, reason);
    } catch (err) {
      options.onError?.(err, reason);
    }
  };

  const unsubs: Array<() => void> = [
    EventBus.on("save:autosave_requested", () => void runAutosave("ink_autosave")),
    EventBus.on("world:long_rest_completed", () => void runAutosave("long_rest")),
    EventBus.on("world:floor_entered", () => void runAutosave("floor_entered")),
    EventBus.on("world:quest_completed", () => void runAutosave("quest_completed")),
  ];

  return {
    dispose: () => {
      for (const off of unsubs) off();
    },
    trigger: runAutosave,
  };
}
